import { normalizeLanguage } from '../locales/index.js';
import { APP_ROUTE_SEO_CONTENT_BN } from './i18n/appRouteSeoContent.bn.js';
import { APP_ROUTE_SEO_CONTENT_HI } from './i18n/appRouteSeoContent.hi.js';
import { APP_ROUTE_SEO_CONTENT_PT } from './i18n/appRouteSeoContent.pt.js';
import { sanitizePublicCreditOnlyContent } from '../utils/publicCreditOnlyContent.js';
import {
  REFERRAL_CREDIT_AMOUNT,
  VECTORIZER_FREE_DAILY_IMAGES,
  WELCOME_CREDIT_AMOUNT
} from './publicOfferFacts.js';

const buildGeneratedRouteMeta = (content = {}) => Object.fromEntries(
  Object.entries(content).map(([path, page]) => {
    const title = String(page?.title || '').trim();
    const intro = Array.isArray(page?.intro) ? page.intro : [];
    return [path, {
      title: /\bstocktag\b/i.test(title) ? title : `${title} | Stocktag`,
      description: String(intro[0] || '').trim()
    }];
  })
);

export const APP_TAB_META = {
  home: {
    title: 'Stocktag | AI Stock Photo Keyword Generator Online',
    description: 'Generate stock photo keywords, titles, and descriptions with AI. Export CSV/XMP for Adobe Stock, Shutterstock, Getty Images, and iStock in one workflow.'
  },
  metadata: {
    title: 'Stock Photo Metadata Studio for Contributors | Stocktag',
    description: 'Generate stock photo titles, descriptions, and keywords in one workflow. Review outputs, edit relevance, and export CSV or XMP for submissions.'
  },
  'market-intelligence': {
    title: 'Stock Keyword Trends and Microstock Calendar | Stocktag',
    description: 'Research stock keyword trends from public search signals, plan uploads with a microstock holiday calendar, and scan similar Adobe Stock or Shutterstock content.'
  },
  'free-credits': {
    title: 'Free Credits for Stock Metadata Workflows | Stocktag',
    description: `Eligible new accounts get ${WELCOME_CREDIT_AMOUNT} free credits, plus ${REFERRAL_CREDIT_AMOUNT} credits per eligible referral. Test titles, descriptions, and keywords on your own stock images.`
  },
  'image-generator': {
    title: 'AI Image Generator for Stock Creator Workflows | Stocktag',
    description: 'Turn prompts and keyword ideas into stock-ready images, then create matching titles, descriptions, and keywords in one connected workflow.'
  },
  vectorizer: {
    title: 'Bulk SVG Image Vectorizer for Creators | Stocktag',
    description: `Convert raster images to clean SVG vectors in bulk in your browser: 7 tracing presets plus custom settings, ZIP download, ${VECTORIZER_FREE_DAILY_IMAGES} free images a day with an account.`
  },
  prompter: {
    title: 'AI Prompt Builder for Image Workflows | Stocktag',
    description: 'Build structured prompts for AI image and video generation with reusable fields and copy-ready output.'
  },
  pricing: {
    title: 'Pricing Plans for Stock Metadata Workflows | Stocktag',
    description: 'Compare monthly credit plans and one-time credit packages for stock metadata production. Choose the capacity that matches your upload volume.'
  },
  feedback: {
    title: 'Feedback Board for Stocktag Product Requests | Stocktag',
    description: 'Browse shared feature requests, workflow friction, and product ideas from Stocktag users in one community board.'
  },
  help: {
    title: 'Help Center for Stock Metadata Workflows | Stocktag',
    description: 'Learn every feature in Stocktag: metadata studio, exports, market intelligence, and image generation workflows.'
  },
  changelog: {
    title: 'Stocktag Changelog for Product Updates | Stocktag',
    description: 'Track product updates, fixes, and workflow improvements across Metadata Studio, exports, prompts, and market intelligence.'
  }
};

const APP_ROUTE_META_TRANSLATIONS = {
  tr: {
    '/metadata': {
      title: 'Stok Metadata Studio ve Dışa Aktarım Aracı | Stocktag',
      description: 'Görsellerinizi yükleyin; başlık, açıklama ve anahtar kelimeleri tek akışta üretin. Sonuçları düzenleyin ve CSV veya XMP olarak dışa aktarın.'
    },
    '/market-intelligence': {
      title: 'Stok Trend ve Talep Analizi Aracı Paneli | Stocktag',
      description: 'Stok içerik için anahtar kelime ivmesini, talep sinyallerini ve kategori hareketliliğini takip edin. Sonraki üretiminizi daha net verilerle planlayın.'
    },
    '/free-credits': {
      title: 'Metadata İş Akışları İçin Ücretsiz Krediler | Stocktag',
      description: `Uygun yeni hesaplara ${WELCOME_CREDIT_AMOUNT} ücretsiz kredi, her uygun davet için ${REFERRAL_CREDIT_AMOUNT} kredi. Başlık, açıklama ve anahtar kelime üretimini kendi stok görsellerinizle test edin.`
    },
    '/image-generator': {
      title: 'Stok İçerik İçin AI Görsel Oluşturma Aracı | Stocktag',
      description: 'AI ile stok odaklı görseller oluşturun, ardından aynı akışta başlık, açıklama ve anahtar kelimeleri birbirine bağlı şekilde üretin.'
    },
    '/vectorizer': {
      title: 'Toplu Görsel Vektörleştirici | Stocktag',
      description: `Raster görselleri tarayıcınızda toplu halde temiz SVG vektörlere dönüştürün: 7 hazır ayar ve özel ayarlar, ZIP indirme, hesabınızla günde ${VECTORIZER_FREE_DAILY_IMAGES} ücretsiz görsel.`
    },
    '/prompter': {
      title: 'Prompter | Stocktag',
      description: 'AI görsel ve video üretimi için tekrar kullanılabilir alanlarla yapılandırılmış promptlar hazırlayın ve çıktıları kolayca kopyalayın.'
    },
    '/pricing': {
      title: 'Fiyatlandırma | Stocktag',
      description: 'Aylık kredi planlarını ve tek seferlik kredi paketlerini karşılaştırın. Üretim hacminize uygun kapasiteyi seçin.'
    },
    '/feedback': {
      title: 'Geri Bildirim Alanı | Stocktag',
      description: 'Kullanıcıların özellik isteklerini, iş akışı sorunlarını ve geliştirme fikirlerini paylaştığı ortak geri bildirim panosu.'
    },
    '/help': {
      title: 'Yardım Merkezi | Stocktag',
      description: 'Stocktag içindeki metadata stüdyosu, dışa aktarma, market intelligence ve görsel üretim akışlarını adım adım öğrenin.'
    },
    '/help/changelog': {
      title: 'Güncellemeler | Stocktag',
      description: 'Stocktag içindeki yeni özellikleri, düzeltmeleri ve iyileştirmeleri takip edin.'
    }
  },
  es: {
    '/metadata': {
      title: 'Studio de metadata stock para contributors | Stocktag',
      description: 'Sube archivos y genera títulos, descripciones y palabras clave en un solo flujo. Revisa los resultados, ajusta la relevancia y exporta en CSV o XMP.'
    },
    '/pricing': {
      title: 'Precios | Stocktag',
      description: 'Compara planes mensuales y paquetes de créditos adicionales según tu flujo de trabajo.'
    },
    '/feedback': {
      title: 'Panel de Feedback | Stocktag',
      description: 'Consulta solicitudes de funciones, fricciones de flujo e ideas de producto compartidas por usuarios de Stocktag en un único tablero comunitario.'
    },
    '/help': {
      title: 'Centro de ayuda | Stocktag',
      description: 'Aprende paso a paso las funciones de Metadata Studio, exportación, market intelligence y generación de imágenes en Stocktag.'
    }
  },
  fr: {
    '/metadata': {
      title: 'Studio metadata stock pour contributeurs | Stocktag',
      description: 'Téléversez vos fichiers et générez titres, descriptions et mots-clés dans un même flux. Relisez, ajustez et exportez en CSV ou XMP.'
    },
    '/pricing': {
      title: 'Tarifs | Stocktag',
      description: 'Comparez les offres mensuelles et les packs de crédits ponctuels selon votre flux de travail.'
    },
    '/feedback': {
      title: 'Tableau de feedback | Stocktag',
      description: 'Parcourez les demandes de fonctionnalités, les frictions de workflow et les idées produit partagées par les utilisateurs de Stocktag dans un tableau communautaire unique.'
    },
    '/help': {
      title: 'Centre d\'aide | Stocktag',
      description: 'Apprenez pas à pas les fonctions de Metadata Studio, l\'export, la market intelligence et la génération d\'images dans Stocktag.'
    }
  },
  de: {
    '/metadata': {
      title: 'Stock-Metadata-Studio für Contributor | Stocktag',
      description: 'Lade Dateien hoch und erstelle Titel, Beschreibungen und Keywords in einem Workflow. Prüfe Ergebnisse und exportiere als CSV oder XMP.'
    },
    '/pricing': {
      title: 'Preise | Stocktag',
      description: 'Vergleiche monatliche Tarife und einmalige Credit-Pakete passend zu deinem Workflow.'
    },
    '/feedback': {
      title: 'Feedback-Board | Stocktag',
      description: 'Durchsuche geteilte Funktionswünsche, Workflow-Reibungspunkte und Produktideen von Stocktag-Nutzern in einem gemeinsamen Community-Board.'
    },
    '/help': {
      title: 'Hilfezentrum | Stocktag',
      description: 'Lerne die Funktionen von Metadata Studio, Exporten, Market Intelligence und Bildgenerierung in Stocktag Schritt für Schritt kennen.'
    }
  },
  it: {
    '/metadata': {
      title: 'Metadata Studio stock per contributor | Stocktag',
      description: 'Carica i file e genera titoli, descrizioni e keyword in un solo flusso. Rivedi i risultati, modifica ed esporta in CSV o XMP.'
    },
    '/market-intelligence': {
      title: 'Trend delle keyword stock e calendario microstock | Stocktag',
      description: 'Monitora keyword in crescita, segnali di domanda e movimenti di categoria per i contenuti stock. Pianifica la produzione con dati piu chiari.'
    },
    '/free-credits': {
      title: 'Crediti gratuiti per workflow metadata | Stocktag',
      description: `I nuovi account idonei ricevono ${WELCOME_CREDIT_AMOUNT} crediti gratuiti, più ${REFERRAL_CREDIT_AMOUNT} crediti per ogni invito valido. Prova titoli, descrizioni e keyword sulle tue immagini stock.`
    },
    '/image-generator': {
      title: 'AI Image Generator per creator e workflow stock | Stocktag',
      description: 'Crea immagini stock-ready con l AI e genera nello stesso workflow titoli, descrizioni e keyword collegati tra loro.'
    },
    '/vectorizer': {
      title: 'Vectorizer | Stocktag',
      description: 'Converti immagini raster in vettori SVG puliti in batch e scarica i file all’istante.'
    },
    '/prompter': {
      title: 'Prompter | Stocktag',
      description: 'Crea prompt strutturati per la generazione AI di immagini e video con campi riutilizzabili e output pronto da copiare.'
    },
    '/pricing': {
      title: 'Prezzi | Stocktag',
      description: 'Confronta piani mensili e pacchetti di crediti una tantum in base al tuo workflow.'
    },
    '/feedback': {
      title: 'Bacheca feedback | Stocktag',
      description: 'Consulta richieste di funzionalità, punti di attrito del workflow e idee di prodotto condivise dagli utenti Stocktag in un’unica board della community.'
    },
    '/help': {
      title: 'Centro assistenza | Stocktag',
      description: 'Scopri passo dopo passo le funzioni di Metadata Studio, export, Market Intelligence e generazione immagini in Stocktag.'
    },
    '/help/changelog': {
      title: 'Changelog | Stocktag',
      description: 'Segui nuove funzioni, correzioni e miglioramenti di Stocktag.'
    }
  },
  id: {
    '/metadata': {
      title: 'Metadata Studio untuk Kontributor Stok | Stocktag',
      description: 'Unggah file dan hasilkan judul, deskripsi, serta keyword dalam satu workflow. Tinjau hasil, edit relevansi, lalu ekspor ke CSV atau XMP.'
    },
    '/market-intelligence': {
      title: 'Tren Keyword Stok dan Kalender Microstock | Stocktag',
      description: 'Pantau keyword yang sedang naik, sinyal permintaan, dan pergerakan kategori untuk konten stok. Rencanakan produksi dengan data yang lebih jelas.'
    },
    '/free-credits': {
      title: 'Kredit Gratis untuk Workflow Metadata Stok | Stocktag',
      description: `Akun baru yang memenuhi syarat mendapat ${WELCOME_CREDIT_AMOUNT} kredit gratis, plus ${REFERRAL_CREDIT_AMOUNT} kredit per referral yang valid. Uji judul, deskripsi, dan keyword pada gambar stok Anda.`
    },
    '/image-generator': {
      title: 'AI Image Generator untuk Workflow Kreator Stok | Stocktag',
      description: 'Buat gambar siap-stok dengan AI lalu hasilkan judul, deskripsi, dan keyword yang saling terhubung dalam workflow yang sama.'
    },
    '/vectorizer': {
      title: 'Bulk SVG Image Vectorizer untuk Kreator | Stocktag',
      description: 'Ubah gambar raster menjadi vektor SVG yang bersih secara batch dan unduh hasilnya seketika.'
    },
    '/prompter': {
      title: 'AI Prompt Builder untuk Workflow Gambar | Stocktag',
      description: 'Buat prompt terstruktur untuk pembuatan gambar dan video AI dengan field yang dapat dipakai ulang serta output yang siap disalin.'
    },
    '/pricing': {
      title: 'Paket Harga untuk Workflow Metadata Stok | Stocktag',
      description: 'Bandingkan paket kredit bulanan dan kredit satu kali untuk produksi metadata stok. Pilih kapasitas yang sesuai dengan volume unggahan Anda.'
    },
    '/feedback': {
      title: 'Papan Feedback untuk Permintaan Produk Stocktag | Stocktag',
      description: 'Telusuri permintaan fitur, hambatan workflow, dan ide produk yang dibagikan pengguna Stocktag dalam satu papan komunitas.'
    },
    '/help': {
      title: 'Pusat Bantuan untuk Workflow Metadata Stok | Stocktag',
      description: 'Pelajari langkah demi langkah fitur Metadata Studio, ekspor, Market Intelligence, dan pembuatan gambar di Stocktag.'
    },
    '/help/changelog': {
      title: 'Changelog Stocktag untuk Pembaruan Produk | Stocktag',
      description: 'Ikuti fitur baru, perbaikan bug, dan peningkatan workflow di Stocktag.'
    }
  },
  th: {
    '/metadata': {
      title: 'Metadata Studio สำหรับคอนทริบิวเตอร์สต็อก | Stocktag',
      description: 'อัปโหลดไฟล์และสร้างชื่อ คำอธิบาย และคีย์เวิร์ดในเวิร์กโฟลว์เดียว ตรวจทานผลลัพธ์ ปรับความเกี่ยวข้อง และส่งออกเป็น CSV หรือ XMP ได้ทันที'
    },
    '/market-intelligence': {
      title: 'เทรนด์คีย์เวิร์ดสต็อกและปฏิทินไมโครสต็อก | Stocktag',
      description: 'ติดตามคีย์เวิร์ดที่กำลังเติบโต สัญญาณอุปสงค์ และการเคลื่อนไหวของหมวดหมู่สำหรับคอนเทนต์สต็อก วางแผนการผลิตด้วยข้อมูลที่ชัดเจนขึ้น'
    },
    '/free-credits': {
      title: 'เครดิตฟรีสำหรับเวิร์กโฟลว์เมตาดาต้าสต็อก | Stocktag',
      description: `บัญชีใหม่ที่ผ่านเงื่อนไขรับเครดิตฟรี ${WELCOME_CREDIT_AMOUNT} เครดิต และรับ ${REFERRAL_CREDIT_AMOUNT} เครดิตต่อการแนะนำที่ผ่านเงื่อนไข ทดสอบชื่อ คำอธิบาย และคีย์เวิร์ดกับภาพสต็อกของคุณเอง`
    },
    '/image-generator': {
      title: 'AI Image Generator สำหรับเวิร์กโฟลว์ครีเอเตอร์สต็อก | Stocktag',
      description: 'สร้างภาพพร้อมใช้สำหรับสต็อกด้วย AI แล้วสร้างชื่อ คำอธิบาย และคีย์เวิร์ดที่เชื่อมกันในเวิร์กโฟลว์เดียว'
    },
    '/vectorizer': {
      title: 'Bulk SVG Image Vectorizer สำหรับครีเอเตอร์ | Stocktag',
      description: 'แปลงภาพแรสเตอร์เป็นเวกเตอร์ SVG แบบสะอาดเป็นชุด และดาวน์โหลดผลลัพธ์ได้ทันที'
    },
    '/prompter': {
      title: 'AI Prompt Builder สำหรับเวิร์กโฟลว์ภาพ | Stocktag',
      description: 'สร้างพรอมป์ตแบบมีโครงสร้างสำหรับการสร้างภาพและวิดีโอด้วย AI ด้วยฟิลด์ที่ใช้ซ้ำได้และผลลัพธ์ที่พร้อมคัดลอก'
    },
    '/pricing': {
      title: 'แพ็กเกจราคาสำหรับเวิร์กโฟลว์เมตาดาต้าสต็อก | Stocktag',
      description: 'เปรียบเทียบแพ็กเกจเครดิตรายเดือนและเครดิตแบบครั้งเดียวสำหรับการผลิตเมตาดาต้าสต็อก เลือกความจุที่เหมาะกับปริมาณอัปโหลดของคุณ'
    },
    '/feedback': {
      title: 'บอร์ด Feedback สำหรับคำขอฟีเจอร์ของ Stocktag | Stocktag',
      description: 'ดูคำขอฟีเจอร์ ปัญหาในเวิร์กโฟลว์ และไอเดียผลิตภัณฑ์จากผู้ใช้ Stocktag ในบอร์ดชุมชนเดียว'
    },
    '/help': {
      title: 'ศูนย์ช่วยเหลือสำหรับเวิร์กโฟลว์เมตาดาต้าสต็อก | Stocktag',
      description: 'เรียนรู้ฟีเจอร์ของ Metadata Studio, การส่งออก, Market Intelligence และการสร้างภาพใน Stocktag แบบทีละขั้น'
    },
    '/help/changelog': {
      title: 'บันทึกการอัปเดตของ Stocktag | Stocktag',
      description: 'ติดตามฟีเจอร์ใหม่ การแก้บั๊ก และการปรับปรุงเวิร์กโฟลว์ใน Stocktag'
    }
  }
};
Object.assign(APP_ROUTE_META_TRANSLATIONS, {
  bn: buildGeneratedRouteMeta(APP_ROUTE_SEO_CONTENT_BN),
  hi: buildGeneratedRouteMeta(APP_ROUTE_SEO_CONTENT_HI),
  pt: buildGeneratedRouteMeta(APP_ROUTE_SEO_CONTENT_PT)
});

export const APP_ROUTE_META_BY_PATH = {
  '/metadata': APP_TAB_META.metadata,
  '/market-intelligence': APP_TAB_META['market-intelligence'],
  '/free-credits': APP_TAB_META['free-credits'],
  '/image-generator': APP_TAB_META['image-generator'],
  '/vectorizer': APP_TAB_META.vectorizer,
  '/prompter': APP_TAB_META.prompter,
  '/pricing': APP_TAB_META.pricing,
  '/feedback': APP_TAB_META.feedback,
  '/help': APP_TAB_META.help,
  '/help/changelog': APP_TAB_META.changelog
};

const APP_TAB_PATH_BY_KEY = {
  metadata: '/metadata',
  'market-intelligence': '/market-intelligence',
  'free-credits': '/free-credits',
  'image-generator': '/image-generator',
  vectorizer: '/vectorizer',
  prompter: '/prompter',
  pricing: '/pricing',
  feedback: '/feedback',
  help: '/help',
  changelog: '/help/changelog'
};

const APP_ROUTE_AVAILABLE_LANGUAGES_BY_PATH = {
  '/metadata': ['en', 'tr', 'es', 'fr', 'de', 'it', 'id', 'th', 'bn', 'hi', 'pt'],
  '/market-intelligence': ['en', 'tr', 'it', 'id', 'th', 'bn', 'hi', 'pt'],
  '/free-credits': ['en', 'tr', 'it', 'id', 'th', 'bn', 'hi', 'pt'],
  '/image-generator': ['en', 'tr', 'it', 'id', 'th', 'bn', 'hi', 'pt'],
  '/vectorizer': ['en', 'tr', 'it', 'id', 'th', 'bn', 'hi', 'pt'],
  '/prompter': ['en', 'tr', 'it', 'id', 'th', 'bn', 'hi', 'pt'],
  '/pricing': ['en', 'tr', 'es', 'fr', 'de', 'it', 'id', 'th', 'bn', 'hi', 'pt'],
  '/feedback': ['en', 'tr', 'es', 'fr', 'de', 'it', 'id', 'th', 'bn', 'hi', 'pt'],
  '/help': ['en', 'tr', 'es', 'fr', 'de', 'it', 'id', 'th', 'bn', 'hi', 'pt'],
  '/help/changelog': ['en', 'tr', 'it', 'id', 'th', 'bn', 'hi', 'pt']
};

const APP_ROUTE_RELATED_PATHS = {
  '/metadata': ['/pricing', '/help', '/feedback'],
  '/market-intelligence': ['/metadata', '/pricing', '/help'],
  '/free-credits': ['/metadata', '/pricing', '/help'],
  '/image-generator': ['/metadata', '/prompter', '/pricing'],
  '/vectorizer': ['/metadata', '/pricing', '/help'],
  '/prompter': ['/vectorizer', '/metadata', '/pricing'],
  '/pricing': ['/metadata', '/help', '/feedback'],
  '/feedback': ['/help', '/metadata', '/pricing'],
  '/help': ['/metadata', '/pricing', '/feedback'],
  '/help/changelog': ['/help', '/feedback', '/metadata']
};

const APP_ROUTE_VISIBLE_COPY = {
  en: {
    eyebrow: 'Workflow overview',
    relatedLabel: 'Related Stocktag workflows',
    dismissLabel: 'Hide this overview'
  },
  tr: {
    eyebrow: 'İş akışına genel bakış',
    relatedLabel: 'İlgili Stocktag iş akışları',
    dismissLabel: 'Bu tanıtımı kapat'
  },
  es: {
    eyebrow: 'Resumen del flujo de trabajo',
    relatedLabel: 'Flujos de trabajo relacionados de Stocktag',
    dismissLabel: 'Ocultar este resumen'
  },
  fr: {
    eyebrow: 'Aperçu du flux de travail',
    relatedLabel: 'Flux de travail Stocktag associés',
    dismissLabel: 'Masquer cet aperçu'
  },
  de: {
    eyebrow: 'Workflow-Überblick',
    relatedLabel: 'Verwandte Stocktag-Workflows',
    dismissLabel: 'Diese Übersicht ausblenden'
  },
  it: {
    eyebrow: 'Panoramica del workflow',
    relatedLabel: 'Workflow Stocktag correlati',
    dismissLabel: 'Nascondi questa panoramica'
  },
  id: {
    eyebrow: 'Ringkasan workflow',
    relatedLabel: 'Workflow Stocktag terkait',
    dismissLabel: 'Sembunyikan ringkasan ini'
  },
  th: {
    eyebrow: 'ภาพรวมเวิร์กโฟลว์',
    relatedLabel: 'เวิร์กโฟลว์ Stocktag ที่เกี่ยวข้อง',
    dismissLabel: 'ซ่อนภาพรวมนี้'
  },
  bn: {
    eyebrow: 'ওয়ার্কফ্লোর সংক্ষিপ্তসার',
    relatedLabel: 'সম্পর্কিত Stocktag ওয়ার্কফ্লো',
    dismissLabel: 'এই সংক্ষিপ্তসার লুকান'
  },
  hi: {
    eyebrow: 'वर्कफ़्लो अवलोकन',
    relatedLabel: 'संबंधित Stocktag वर्कफ़्लो',
    dismissLabel: 'यह अवलोकन छिपाएँ'
  },
  pt: {
    eyebrow: 'Visão geral do fluxo de trabalho',
    relatedLabel: 'Fluxos de trabalho relacionados do Stocktag',
    dismissLabel: 'Ocultar esta visão geral'
  }
};

const stripStocktagSuffix = (title = '') => (
  String(title).replace(/\s*\|\s*Stocktag(?:\s+AI)?\s*$/i, '').trim()
);

export const getAppRouteAvailableLanguages = (path) => (
  APP_ROUTE_AVAILABLE_LANGUAGES_BY_PATH[path]
    ? [...APP_ROUTE_AVAILABLE_LANGUAGES_BY_PATH[path]]
    : ['en']
);

export const resolveAppRouteSeoLanguage = (path, language) => {
  const normalizedLanguage = normalizeLanguage(language);
  const availableLanguages = getAppRouteAvailableLanguages(path);
  return availableLanguages.includes(normalizedLanguage) ? normalizedLanguage : 'en';
};

export const getAppRouteMetaByPath = (path, language = 'en') => {
  const baseMeta = APP_ROUTE_META_BY_PATH[path];
  if (!baseMeta) return null;
  const resolvedLanguage = resolveAppRouteSeoLanguage(path, language);
  const localizedMeta = resolvedLanguage === 'en'
    ? baseMeta
    : (APP_ROUTE_META_TRANSLATIONS[resolvedLanguage]?.[path] || baseMeta);
  return sanitizePublicCreditOnlyContent(localizedMeta, resolvedLanguage);
};

export const getAppRouteVisibleContentByPath = (path, language = 'en') => {
  const resolvedLanguage = resolveAppRouteSeoLanguage(path, language);
  const meta = getAppRouteMetaByPath(path, resolvedLanguage);
  if (!meta) return null;

  const copy = APP_ROUTE_VISIBLE_COPY[resolvedLanguage] || APP_ROUTE_VISIBLE_COPY.en;
  const relatedLinks = (APP_ROUTE_RELATED_PATHS[path] || [])
    .map((relatedPath) => {
      const relatedMeta = getAppRouteMetaByPath(relatedPath, resolvedLanguage);
      if (!relatedMeta) return null;
      return {
        path: relatedPath,
        label: stripStocktagSuffix(relatedMeta.title)
      };
    })
    .filter(Boolean);

  return {
    description: meta.description,
    dismissLabel: copy.dismissLabel,
    eyebrow: copy.eyebrow,
    heading: stripStocktagSuffix(meta.title),
    relatedLabel: copy.relatedLabel,
    relatedLinks,
    resolvedLanguage
  };
};

export const getAppTabMeta = (tab, language = 'en') => {
  if (tab === 'home') return APP_TAB_META.home;
  const path = APP_TAB_PATH_BY_KEY[tab];
  return getAppRouteMetaByPath(path, language) || APP_TAB_META[tab] || APP_TAB_META.home;
};

export const PUBLIC_INDEXABLE_APP_PATHS = new Set(
  Object.keys(APP_ROUTE_META_BY_PATH).filter((path) => (
    path !== '/image-generator' && path !== '/prompter'
  ))
);

// Restricted routes still need an SSR shell so entitled users can sign in and
// hydrate the app, but they must not re-enter public discovery surfaces.
export const PRERENDER_APP_PATHS = new Set(Object.keys(APP_ROUTE_META_BY_PATH));
